import {css, StyleSheet} from 'aphrodite';
import {getNamedType} from 'query-api-graphql';
import React, {useCallback} from 'react';

import GraphQLFieldSelectorWrapper from 'analysis-tool/data-frame-creation/schemas/GraphQLFieldSelectorWrapper';
import QueryApiAllSchemaSelector, {
  QueryApiSchemaSelectorSchema,
} from 'analysis-tool/data-frame-creation/schemas/QueryApiAllSchemaSelector';
import {FormState} from 'analysis-tool/data-frame-creation/schemas/RegistrySchemaDataFrameCreationTypes';
// TODO(BNCH-150380): Organize into pipelines/look-up/ module
// eslint-disable-next-line import-access/jsdoc
import RelatedResultsFormList from 'analysis-tool/data-frame-creation/schemas/RelatedResultsFormList';
import {PLATE_SCHEMA_FIELD_SELECTOR_LOGICAL_DEPTH_LIMIT} from 'analysis-tool/data-frame-creation/schemas/SchemaDatasetCreationConstants';
import {getFieldDisplayNameFn} from 'analysis-tool/data-frame-creation/schemas/SchemaDatasetCreationUtils';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import countSelectedFields from 'query-api/graphql-field-selector/countSelectedFields';
import {fetchQueryApiTypeExtension, QueryApiIntrospection} from 'query-api/useQueryApiIntrospection';
import BetaBadge from 'ui-library/badges/BetaBadge';
import APIUtil from 'util/APIUtil';
import pick from 'util/pick';

import {VariableHeaderOrNull} from '../StepConfigFieldsTemplate';
import {buildInitialQueryFormStateFromQueryString} from './LookUpQueryUtils';

interface LookUpSchemaSelectorStepperProps {
  setQueryFormState: (state: FormState) => void;
  queryApiIntrospection: QueryApiIntrospection;
  queryFormState: FormState;
  disabled?: boolean;
}

export default function LookUpSchemaSelectorStepper({
  setQueryFormState,
  queryApiIntrospection,
  queryFormState,
  disabled = false,
}: LookUpSchemaSelectorStepperProps): JSX.Element {
  const queryListingField =
    queryFormState.entityListingFieldName == null
      ? null
      : queryApiIntrospection.getQueryField(queryFormState.entityListingFieldName);

  const schemaId =
    queryFormState.entityListingFieldName == null
      ? null
      : queryApiIntrospection.getQueryFieldSchemaId(queryFormState.entityListingFieldName);

  const handleChangeSchema = useCallback(
    (schema: QueryApiSchemaSelectorSchema | null): void => {
      if (schema == null) {
        setQueryFormState({entityListingFieldName: null});
        return;
      }

      const {formState} = buildInitialQueryFormStateFromQueryString(schema.id, null, queryApiIntrospection);
      setQueryFormState(formState);

      // Load type extension for the new schema, so the related results backlink fields are available.
      // Currently, only entity schemas are supported by the query-api-backlink-type-extension endpoint.
      if (APIUtil.isTagSchema(schema.id) && formState.entityListingFieldName != null) {
        const listingField = queryApiIntrospection.getQueryField(formState.entityListingFieldName);
        if (listingField != null) {
          fetchQueryApiTypeExtension(getNamedType(listingField.type).name);
        }
      }
    },
    [queryApiIntrospection, setQueryFormState]
  );

  return (
    <div>
      <QueryApiAllSchemaSelector
        schemaId={schemaId}
        handleSchemaChange={handleChangeSchema}
        queryApiIntrospection={queryApiIntrospection}
        schemaFilter={(schema) =>
          APIUtil.isPlateSchema(schema.id) ||
          APIUtil.isTagSchema(schema.id) ||
          APIUtil.isContainerSchema(schema.id)
        }
        disabled={disabled}
      />
      {schemaId != null && queryFormState.entityListingFieldName != null && queryListingField != null && (
        <>
          <div className={css(styles.section)}>
            {(() => {
              const numSelected = countSelectedFields(queryFormState.entityFieldSelections.selections);
              return (
                <VariableHeaderOrNull
                  variableName={`Select columns (${numSelected} ${pluralize(numSelected, 'column')} selected)`}
                  isCompact={true}
                />
              );
            })()}
            <GraphQLFieldSelectorWrapper
              field={queryListingField}
              logicalDepthLimit={PLATE_SCHEMA_FIELD_SELECTOR_LOGICAL_DEPTH_LIMIT}
              selectionSet={queryFormState.entityFieldSelections}
              setSelectionSet={(newSelectionSet) => {
                setQueryFormState({...queryFormState, entityFieldSelections: newSelectionSet});
              }}
              getFieldDisplayName={getFieldDisplayNameFn(queryListingField, queryApiIntrospection)}
              hideHeader={true}
              {...pick(
                queryApiIntrospection,
                'getTypeDisplayName',
                'graphQLSchema',
                'isDeprecatedField',
                'isBacklinkField',
                'isUserDefinedField',
                'isUserComputedField'
              )}
            />
          </div>
          {BenchlingConfig.ENABLE_INSIGHTS_LABS && APIUtil.isTagSchema(schemaId) && (
            // TODO(BNCH-150380): Improve UI and move out from Insights Labs
            <div className={css(styles.section)}>
              <div className={css(styles.headerWithBadge)}>
                <VariableHeaderOrNull variableName="Add Results" isCompact={false} />
                <BetaBadge />
              </div>
              <RelatedResultsFormList
                relatedResultsFormStates={queryFormState.relatedResultsFormStates}
                onChangeRelatedResultFormStates={(newRelatedResultsFormStates) => {
                  setQueryFormState({
                    ...queryFormState,
                    relatedResultsFormStates: newRelatedResultsFormStates,
                  });
                }}
                entityListingField={queryListingField}
                queryApiIntrospection={queryApiIntrospection}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  section: {
    marginTop: 11,
  },
  headerWithBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
});
